import { AppConfig } from '@dh-io-emh/config-types';
import { asFunction, asValue, createContainer, Resolver } from 'awilix';
import { B2xCosApi, createB2xCosApi } from './api/b2x-cos.api';
import { CartApi, createCartApi } from './api/cart.api';
import { createToasApi, ToasApi } from './api/toas.api';
import { createDealerApi, DealerApi } from './api/dealer.api';
import { createDeepLinkingApi, DeepLinkingApi } from './api/deep-linking.api';
import { createOrderApi, OrderApi } from './api/order.api';
import { createPCPConfigApi, PcpApi } from './api/pcp-configuration.api';
import { createPOSApi, PosApi } from './api/point-of-services.api';
import { createProductApi, ProductApi } from './api/product';
import { createUserApi, UserApi } from './api/user.api';
import { createVDSApi, VdsApi } from './api/vds.api';
import { AuthService, createAuthService } from './auth';
import { createRouter, RouterService } from './router';
import { createI18nService, I18nService } from './i18n/i18n.service';
import { AbstractRouterOptions } from './router/types';
import { App } from 'vue';
import { createTrackingService, TrackingService } from './tracking';
import { ProductExperienceApi } from '@dh-io-emh/commerce-api/src/models/product/product.api';
import { SearchExperienceApi } from '@dh-io-emh/commerce-api/src/models/search/search.api';
import { SearchFilterExperienceApi } from '@dh-io-emh/commerce-api/src/models/search-filter/search-filter.api';
import { productExperienceAdapterFactory } from '@dh-io-emh/commerce-api/src/adapters/product/product-exp-adapter-factory';
import { searchExperienceAdapterFactory } from '@dh-io-emh/commerce-api/src/adapters/search/search-exp-adapter-factory';
import { searchFilterExperienceAdapterFactory } from '@dh-io-emh/commerce-api/src/adapters/search-filter/search-filter-exp-adapter-factory';
import { getCommerceApiCommonOptions, getCommerceApiProductOptions } from './commerce-api-injector';
import { Logger } from './utils/logger.util';

export type Services = {
  config: AppConfig;
  b2xCosApi: B2xCosApi;
  cartApi: CartApi;
  toasApi: ToasApi;
  dealerApi: DealerApi;
  orderApi: OrderApi;
  pcpConfigApi: PcpApi;
  productExperienceApi: ProductExperienceApi;
  searchExperienceApi: SearchExperienceApi;
  searchFilterExperienceApi: SearchFilterExperienceApi;
  posApi: PosApi;
  productApi: ProductApi;
  userApi: UserApi;
  vdsApi: VdsApi;
  trackingService: TrackingService;
  router: RouterService;
  authService: AuthService;
  i18nService: I18nService;
  deepLinkingApi: DeepLinkingApi;
};

type DependencyRegister = {
  [Property in keyof Services]: Resolver<Services[Property]>;
};

const getRegisters = async (
  config: AppConfig,
  routerOptions: AbstractRouterOptions,
  app: App,
): Promise<Omit<DependencyRegister, 'config'>> => {
  const i18nService = await createI18nService(config, app);

  return {
    i18nService: asValue(i18nService),
    b2xCosApi: asFunction(createB2xCosApi).singleton(),
    productExperienceApi: asFunction(productExperienceAdapterFactory)
      .inject(() => getCommerceApiProductOptions(config))
      .singleton(),
    searchExperienceApi: asFunction(searchExperienceAdapterFactory)
      .inject(() => getCommerceApiCommonOptions(config))
      .singleton(),
    searchFilterExperienceApi: asFunction(searchFilterExperienceAdapterFactory)
      .inject(() => getCommerceApiCommonOptions(config))
      .singleton(),
    cartApi: asFunction(createCartApi).singleton(),
    toasApi: asFunction(createToasApi).singleton(),
    dealerApi: asFunction(createDealerApi).singleton(),
    orderApi: asFunction(createOrderApi).singleton(),
    pcpConfigApi: asFunction(createPCPConfigApi).singleton(),
    posApi: asFunction(createPOSApi).singleton(),
    productApi: asFunction(createProductApi).singleton(),
    userApi: asFunction(createUserApi).singleton(),
    vdsApi: asFunction(createVDSApi).singleton(),
    authService: asFunction(createAuthService).singleton(),
    deepLinkingApi: asFunction(createDeepLinkingApi).singleton(),
    router: asFunction(createRouter)
      .inject(() => ({ routerOptions }))
      .singleton(),
    trackingService: asFunction(createTrackingService)
      .inject(() => ({
        appVersion: process.env.VUE_APP_VERSION,
      }))
      .singleton(),
  };
};

const container = createContainer();

export const initContainer = async (config: AppConfig, routerOptions: AbstractRouterOptions, app: App) => {
  container.register('config', asValue(config));

  const services = await getRegisters(config, routerOptions, app);

  container.register(services);
};

export const initTestContainer = (config: AppConfig, mockServices: Omit<Services, 'config'>) => {
  container.register('config', asValue(config));
  Object.entries(mockServices).forEach(([key, service]) => {
    container.register(key, asValue(service));
  });
};

export const disposeContainer = () => container.dispose();

export const infuse = <T extends Services[K], K extends keyof Services>(register: K): T => {
  try {
    return container.resolve<T>(register);
  } catch {
    Logger.error(`ioc-config::infuse() -> "${register}" service not found`);

    return {} as T;
  }
};
